import { useEffect, useMemo, useRef, useState } from "react";
import { Unlock } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getBulkDropdownData } from "../../api/keyvalue";
import {
    addSupplierPaymentHoldData,
    getSupplierPaymentHoldList,
    unholdSupplierPaymentHold,
} from "../../api/finance/supplierPaymentHoldApi";
import Grid from "../../components/Grid";
import Button from "../../components/input/Button";
import LoadingSpinner from "../../components/LoadingSpinner";
import { DateOnlyCellRenderer, StatusCellStyle } from "../../components/renderer/StyleRenderer";
import { FORMS, MODULES } from "../../const";
import { errorActions } from "../../store/error-slice";
import SupplierPaymentHoldComponent from "../../components/finance/SupplierPyamentHold/SupplierPaymentHoldComponent";
import { supplierPaymentHoldValidation } from "../../components/validations/SupplierPaymentHoldCreation";
import useUserCompany from "../../hooks/useUserCompany";
import { getFieldAuthorityKeys } from "../../auth/authHelper";

const FORM = FORMS.SUPPLIER_PAYMENT_HOLD.HEADER.form;

const SupplierPaymentHold = () => {
    const dispatch = useDispatch();
    const errorState = useSelector((state) => state.error.supplierHoldErrors) || {};
    const user = useSelector((state) => state.user);
    const permissions = useSelector((state) => state.user.permissions);
    const userCompany = useUserCompany();

    const [isListLoading, setIsListLoading] = useState(false);
    const [holdList, setHoldList] = useState([]);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [isUnholdLoading, setIsUnholdLoading] = useState(false);
    const [evInvTypeOptions, setEvInvTypeOptions] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const [filterRefType, setFilterRefType] = useState(1);

    const supplierPaymentHoldRef = useRef();

    const financeAuth = permissions?.[MODULES.Finance];
    const evInvAuthKeys = useMemo(
        () => getFieldAuthorityKeys(financeAuth, FORM, "sph_ev_inv_type"),
        [financeAuth]
    );

    const allowedEvInvOptions = useMemo(() => {
        if (!evInvTypeOptions || evInvTypeOptions.length === 0) return [];
        return evInvTypeOptions.filter((opt) =>
            evInvAuthKeys.includes(`PAYMENT_HOLD_${String(opt.text).toUpperCase()}`)
        );
    }, [evInvTypeOptions, evInvAuthKeys]);

    useEffect(() => {
        dispatch(errorActions.clearSupplierHoldError());
    }, []);

    const loadList = async (refType = filterRefType) => {
        setIsListLoading(true);
        const evInvFilterValue =
            allowedEvInvOptions.length === 1 ? allowedEvInvOptions[0].value : null;
        const response = await getSupplierPaymentHoldList({
            sphRefType: refType,
            sphEvInvType: evInvFilterValue,
            company: userCompany ? userCompany.comId : null,
        });
        setHoldList(response || []);
        setSelectedRows([]);
        setIsListLoading(false);
    };

    useEffect(() => {
        (async () => {
            const { ev_inv_type: evInvType } = await getBulkDropdownData([
                { key: "ev_inv_type" },
            ]);
            setEvInvTypeOptions(evInvType);
        })();
    }, []);

    useEffect(() => {
        if (evInvTypeOptions.length > 0) {
            loadList(filterRefType);
        }
    }, [allowedEvInvOptions]);

    const handleFilterChange = (e, data) => {
        setFilterRefType(data.value);
        loadList(data.value);
    };

    const reset = () => supplierPaymentHoldRef.current.resetForm();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitLoading(true);
        dispatch(errorActions.clearSupplierHoldError());

        const data = supplierPaymentHoldRef.current.getData();
        const errors = supplierPaymentHoldValidation(data);

        if (Object.keys(errors).length > 0) {
            dispatch(errorActions.validateSupplierHold({ supplierHoldRequest: data }));
            setIsSubmitLoading(false);
            toast.error("There was an error adding Payment Hold", {
                autoClose: 5000,
                containerId: "Main",
            });
            return;
        }

        await addSupplierPaymentHoldData({
            supplierPaymentHoldRequest: {
                ...data,
                sphCompany: userCompany ? userCompany.comId : null,
            },
            author: user.username,
        });

        toast.success("Payment Hold added successfully!", {
            autoClose: 5000,
            containerId: "Main",
        });

        setIsSubmitLoading(false);
        reset();
        loadList();
    };

    const handleUnhold = async () => {
        const idsToUnhold = selectedRows
            .filter((row) => row.sphStatus === 2)
            .map((row) => row.sphId);

        if (idsToUnhold.length === 0) {
            toast.error("Selected records are already released.", {
                autoClose: 5000,
                containerId: "Main",
            });
            return;
        }

        setIsUnholdLoading(true);
        await unholdSupplierPaymentHold({
            sphIds: idsToUnhold,
            author: user.username,
        });
        toast.success("Selected holds released successfully!", {
            autoClose: 5000,
            containerId: "Main",
        });
        setIsUnholdLoading(false);
        loadList();
    };

    return (
        <>
            <form className="ui form" onSubmit={handleSubmit}>
                <div className="ui segment">
                    <SupplierPaymentHoldComponent
                        ref={supplierPaymentHoldRef}
                        isLoading={false}
                        errors={errorState.supplierHoldErrors || {}}
                        dropdown={{ evInvTypeOptions: allowedEvInvOptions }}
                        editMode={false}
                    />
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: "10px",
                        }}
                    >
                        <Button
                            type="submit"
                            template="create"
                            isLoading={isSubmitLoading}
                            create={true}
                            form={FORM}
                            module={MODULES.Finance}
                            authenticated={true}
                            editMode={false}
                        />
                    </div>
                </div>
            </form>

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    margin: "20px 0 10px",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{ fontWeight: 600, color: "#555" }}>Show:</span>
                    <div className="ui buttons">
                        <button
                            type="button"
                            className={`ui button ${filterRefType === 1 ? "blue" : ""}`}
                            onClick={() => handleFilterChange(null, { value: 1 })}
                        >
                            Supplier
                        </button>
                        <button
                            type="button"
                            className={`ui button ${filterRefType === 2 ? "blue" : ""}`}
                            onClick={() => handleFilterChange(null, { value: 2 })}
                        >
                            Job
                        </button>
                    </div>
                </div>

                <Button
                    template="custom"
                    label="Release Selected"
                    Icon={Unlock}
                    onClick={handleUnhold}
                    disabled={selectedRows.length === 0}
                    isLoading={isUnholdLoading}
                />
            </div>

            {isListLoading ? (
                <LoadingSpinner />
            ) : (
                <Grid
                    isLoading={isListLoading}
                    content={holdList}
                    multiRowSelection={true}
                    onSelectionChanged={(props) =>
                        setSelectedRows(props.api.getSelectedRows())
                    }
                    config={[
                        {
                            headerName: "",
                            checkboxSelection: true,
                            width: 80,
                            minWidth: 40,
                            maxWidth: 80,
                            pinned: "left",
                            suppressSizeToFit: true,
                        },
                        { headerName: "Id", field: "sphId", hide: true },
                        { headerName: "Code", field: "sphCode" },
                        { headerName: "EV/INV", field: "evInvTypeName" },
                        { headerName: "Type", field: "refTypeName" },
                        { headerName: "Reference", field: "referenceDisplay" },
                        { headerName: "Reason", field: "sphReason" },
                        {
                            headerName: "Status",
                            field: "statusName",
                            cellRenderer: StatusCellStyle,
                        },
                        { headerName: "Created By", field: "createdBy" },
                        {
                            headerName: "Created Date",
                            field: "createdDate",
                            cellRenderer: DateOnlyCellRenderer,
                        },
                    ]}
                />
            )}
        </>
    );
};

export default SupplierPaymentHold;